import React, { useState, useEffect } from "react";
import { OrderSummaryProps } from "../../utils/types";
import { LABELS } from "./OrderSummaryConstants";
import PaymentMethods from "./OrderSummaryPaymentMethods";
import OrderHeader from "./OrderSummaryOrderHeader";
import OrderStatusSelector from "./OrderSummaryOrderStatus";
import OrderSummaryActions from "./OrderSummaryActions";
import { notifySuccess } from "src/assets/toastService";
import { splitDateTime } from "src/shared/components/dateTime";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import { useUser } from "src/shared/context/UserContext";

const SummaryItem: React.FC<{ label: string; value: string | number | undefined }> = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 px-3 border-b border-gray-100 last:border-b-0">
    <span className="text-gray-600 text-sm font-medium">{label}</span>
    <span className="text-gray-900 font-semibold text-sm">{value ?? "0.00"}</span>
  </div>
);

interface ExtendedOrderSummaryProps extends OrderSummaryProps {
  openCashModal: () => void;
  openCreditModal: () => void;
}

const OrderSummary: React.FC<ExtendedOrderSummaryProps> = ({
  order,
  status,
  handleStatusChange,
  paymentMethod,
  handlePaymentMethodChange,
  roundValue,
  cancelOrder,
  isCancelDisabled,
  handlePrint,
  openCashModal,
  openCreditModal,
}) => {
  const { user } = useUser();
  const [refundProcessed, setRefundProcessed] = useState(false);
  const [updatedReceivedAmount, setUpdatedReceivedAmount] = useState(0);

  const { bgClass, fontClass } = useUserTheme();

  const latestOrder = order?.orders?.length ? order.orders[order.orders.length - 1] : undefined;

  const remainingAmount = Number(latestOrder?.remainingAmount || 0);

  const originalReceivedAmount = order?.paymentHistory?.reduce((sum: number, p: any) => sum + Number(p.receivedAmount || 0), 0) || 0;
  const isCancelled = latestOrder?.status === "Cancelled";

  const currentOrderStatus = latestOrder?.status || status;

  const isFullyPaid = remainingAmount <= 0;

  const actualReceivedAmount = refundProcessed ? updatedReceivedAmount : originalReceivedAmount;

  const showRefundOption = originalReceivedAmount > 0 && !isCancelled;

  useEffect(() => {
    if (currentOrderStatus !== "Cancelled") {
      setRefundProcessed(false);
      setUpdatedReceivedAmount(originalReceivedAmount);
    }
  }, [currentOrderStatus, originalReceivedAmount]);

  const { date: orderDate, time: orderTime } = splitDateTime(order?.orders?.[0]?.orderedOn?.$date ?? "");

  const finalAmount = order?.orders?.[0]?.finalAmount || order?.orders?.[0]?.orderAmount || 0;

  const summaryItems = [
    { label: LABELS.ORDER_DATE, value: orderDate },
    { label: LABELS.ORDER_TIME, value: orderTime },
    { label: LABELS.PAYMENT_MODE, value: order?.paymentHistory?.[0]?.paymentMethod || "-" },
    {
      label: LABELS.SUBTOTAL,
      value: `${roundValue(finalAmount)} QAR`,
    },
    {
      label: LABELS.RECEIVED_PRICE,
      value: `${refundProcessed ? "0.00" : roundValue(actualReceivedAmount)} QAR${refundProcessed ? " (Refunded)" : ""}`,
    },
    {
      label: LABELS.REMAINING_PRICE,
      value: `${roundValue(remainingAmount)} QAR`,
    },
  ];

  const handleDeliveredClick = () => {
    const syntheticEvent = {
      target: {
        value: "Delivered",
      },
    } as React.ChangeEvent<{ value: string }>;
    handleStatusChange(syntheticEvent);
  };

  const handleCancelWithRefund = (processRefund: boolean) => {
    if (processRefund && originalReceivedAmount > 0) {
      setRefundProcessed(true);
      setUpdatedReceivedAmount(0);
    } else {
      notifySuccess("Order cancelled successfully");
    }

    cancelOrder();
  };

  return (
    <div className={`${fontClass} h-full flex flex-col`}>
      <div className={`${bgClass} text-white px-4 py-2 rounded-t flex-shrink-0`}>
        <h2 className="text-lg font-bold">{LABELS.ORDER_SUMMARY}</h2>
      </div>

      <div className="p-3 space-y-3 flex-1 overflow-y-auto">
        <OrderHeader orderId={order?.orders?.[0]?.orderId} customerName={order?.customerDetails?.firstName} />

        <div className="bg-gray-50 rounded border border-gray-200 overflow-hidden">
          {summaryItems.map(({ label, value }, i) => (
            <SummaryItem key={i} label={label} value={value} />
          ))}
        </div>

        <OrderStatusSelector
          status={status}
          orderStatus={currentOrderStatus}
          remainingAmount={remainingAmount}
          isFullyPaid={isFullyPaid}
          onChange={handleStatusChange}
          onDeliveredClick={handleDeliveredClick}
        />

        <PaymentMethods
          selected={paymentMethod}
          onChange={handlePaymentMethodChange}
          remainingAmount={remainingAmount}
          status={status}
          isFullyPaid={isFullyPaid}
          orderStatus={currentOrderStatus}
          openCashModal={openCashModal}
          openCreditModal={openCreditModal}
        />

        <div className="pt-2">
          <OrderSummaryActions
            handlePrint={handlePrint}
            cancelOrder={handleCancelWithRefund}
            isCancelDisabled={isCancelDisabled}
            orderStatus={currentOrderStatus}
            showRefundOption={showRefundOption}
            receivedAmount={originalReceivedAmount}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
